<template>
  <ErrorPage v-if="!canManagePortal" type="forbidden" />
  <ErrorPage v-else-if="isNotFound" type="notFound" />
  <LoadingWrapper v-else :state="status">
    <div class="no-print flex justify-end p-4">
      <BaseTextButton color="filled" size="M" icon="print" @click="printSlips">
        {{ $t('portal.print.print') }}
      </BaseTextButton>
    </div>
    <p v-if="members.length === 0" class="text-body-large text-on-surface-variant px-8">
      {{ $t('portal.members.empty') }}
    </p>
    <section
      v-for="(member, index) in members"
      :key="member.memberId"
      class="flex flex-col gap-6 break-inside-avoid px-8 py-16"
      :class="index > 0 && 'break-before-page'"
    >
      <p v-if="teamName" class="text-title-medium text-on-surface-variant">{{ teamName }}</p>
      <h1 class="text-display-small">{{ memberName(member) }}</h1>
      <div class="flex flex-col gap-1">
        <p class="text-label-large text-on-surface-variant">{{ $t('portal.link.title') }}</p>
        <p class="text-title-large break-all">{{ portalUrl ?? $t('portal.print.noLink') }}</p>
      </div>
      <div class="flex flex-col gap-1">
        <p class="text-label-large text-on-surface-variant">
          {{ $t('organization.loginLessMember.accessCode.title') }}
        </p>
        <p class="text-display-small tracking-widest">
          {{ member.accessCode ? formatAccessCode(member.accessCode) : $t('portal.print.noCode') }}
        </p>
      </div>
    </section>
  </LoadingWrapper>
</template>

<script lang="ts" setup>
  import { computed } from 'vue'
  import { useRoute } from 'vue-router'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { usePermission } from '@/core/composables/use-permission'
  import { Permission } from '@/core/models/user'
  import ErrorPage from '@/modules/app/pages/ErrorPage.vue'
  import { useTeamPortal } from '../composables/use-team-portal'
  import type { TeamPortalMember } from '../models/team-portal'
  import { formatAccessCode } from '../utils/access-code'

  const route = useRoute()
  const { can } = usePermission()
  const canManagePortal = computed(() => can(Permission.PORTAL_MANAGE))
  const teamId = computed(() => route.params.teamId as string)

  const { teamName, portalUrl, members, status, isNotFound } = useTeamPortal(teamId, {
    autoload: canManagePortal.value,
  })

  const memberName = (member: TeamPortalMember) => {
    return [member.firstName, member.lastName].filter(Boolean).join(' ')
  }

  const printSlips = () => {
    window.print()
  }
</script>
